import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { hasWarehouseAssignment } from '@gonfalon/experiments/hasWarehouseAssignment';
import type { DraftExperimentIterationProps, Experiment } from '@gonfalon/experiments/types';
import { ExperimentStatusKind } from '@gonfalon/experiments/types';
import type { FeatureFlag } from '@gonfalon/flags/types';
import { toExperimentResults, toHoldoutDetails, toMultiArmedBanditResults } from '@gonfalon/navigator/navigators';
import { getFeatureFlagQuery } from '@gonfalon/rest-api/queries/flags';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Dialog, Modal, ModalOverlay } from '@launchpad-ui/components';
import { useSuspenseQuery } from '@tanstack/react-query';

import { useUpdateExperimentStatusCallbacks } from '#components/experimentation/common/hooks/useUpdateExperimentStatusCallbacks';

import { SaveExperimentModal } from './SaveExperimentModal';
import { StartExperimentModal } from './StartExperimentModal';

export type ConfirmOptions = {
  /** When true, includes startIteration instruction in the same API call (for running experiments) */
  saveAndStart?: boolean;
  /** Reason/justification for the change (used with saveAndStart) */
  changeJustification?: string;
  /** Callback to invoke on successful start (only used with saveAndStart) */
  onSuccess?: () => void;
};

export type UpdateExperimentModalProps = {
  isEditMode?: boolean;
  isLoading?: boolean;
  experiment: Experiment | undefined;
  isModalOpen: boolean;
  onCancel: () => void;
  onConfirm: (options?: ConfirmOptions) => Promise<void> | void;
  setIsModalOpen: (isOpen: boolean) => void;
  onStartSuccess?: () => void;
  draftIterationProps?: Partial<DraftExperimentIterationProps>;
};

export function UpdateExperimentModal(props: UpdateExperimentModalProps) {
  const { experiment, draftIterationProps } = props;
  // Prioritize draft flag key if it exists, otherwise use current iteration's flag
  const flagKey =
    draftIterationProps?.flagConfig?.flagKey ??
    (experiment ? Object.keys(experiment?.currentIteration?.flags ?? {})[0] : '');

  // Experiments that assign from a warehouse source have no flag. Requesting an empty key fails
  // with "Invalid resource identifier", which would take the whole builder down.
  return flagKey ? (
    <UpdateExperimentModalWithFlag {...props} flagKey={flagKey} />
  ) : (
    <UpdateExperimentModalContent {...props} />
  );
}

function UpdateExperimentModalWithFlag({ flagKey, ...props }: UpdateExperimentModalProps & { flagKey: string }) {
  const envKey = useSelectedEnvironmentKey();
  const projectKey = useProjectKey();

  const { data: flagData } = useSuspenseQuery(
    getFeatureFlagQuery({
      projectKey,
      featureFlagKey: flagKey,
      query: {
        env: [envKey],
      },
    }),
  );

  return <UpdateExperimentModalContent {...props} flagKey={flagKey} flagData={flagData} />;
}

function UpdateExperimentModalContent({
  isEditMode = false,
  isLoading = false,
  isModalOpen,
  experiment,
  onCancel,
  onConfirm,
  onStartSuccess,
  setIsModalOpen,
  draftIterationProps,
  flagKey,
  flagData,
}: UpdateExperimentModalProps & { flagKey?: string; flagData?: FeatureFlag }) {
  const [isUpdateExperimentStatusCallbacksPending, setIsUpdateExperimentStatusCallbacksPending] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const envKey = useSelectedEnvironmentKey();
  const projectKey = useProjectKey();

  const iterationFlag = flagKey
    ? (experiment?.draftIteration?.flags?.[flagKey] ?? experiment?.currentIteration?.flags?.[flagKey])
    : undefined;

  const { startExperimentIteration, isPending: isUpdateExperimentPending } = useUpdateExperimentStatusCallbacks({
    experiment,
    projectKey,
    environmentKey: envKey,
  });
  const isPending = isUpdateExperimentPending || isLoading || isUpdateExperimentStatusCallbacksPending;
  const isMAB = experiment?.type === 'mab';
  const isHoldout = experiment?.type === 'holdout';
  const hasStratifiedSampling = draftIterationProps?.isUsingStratifiedSampling;
  const isRunning = experiment?.currentIteration?.status === ExperimentStatusKind.RUNNING;
  const isFlagOff = flagData && !flagData.environments?.[envKey]?.on;
  const assignsFromWarehouse = hasWarehouseAssignment(
    experiment,
    experiment?.draftIteration ?? experiment?.currentIteration,
  );

  const handleOpenChange = (isOpen: boolean) => !isOpen && setIsModalOpen(false);

  const handleStartExperiment = useCallback(
    async (createNewIteration: boolean, options?: { reason?: string }) => {
      setIsUpdateExperimentStatusCallbacksPending(true);
      try {
        // For running experiments, combine stop + updateFields + start in one API call
        if (isRunning) {
          // Pass onStartSuccess to onConfirm so it's called only on actual API success
          // (form.handleSubmit doesn't throw on errors, so we can't rely on await)
          await onConfirm({
            saveAndStart: true,
            changeJustification: options?.reason,
            onSuccess: onStartSuccess,
          });
        } else {
          await onConfirm();
          await startExperimentIteration(options?.reason, createNewIteration, onStartSuccess);
        }
        setIsModalOpen(false);

        if (isHoldout && !location.pathname.includes('/details')) {
          await navigate(
            toHoldoutDetails({
              projectKey,
              holdoutKey: experiment?.key ?? '',
              environmentKey: envKey,
            }),
          );
        }

        if (!isHoldout && !location.pathname.includes('/results')) {
          await navigate(
            isMAB
              ? toMultiArmedBanditResults({
                  projectKey,
                  multiArmedBanditKey: experiment?.key ?? '',
                  environmentKey: envKey,
                })
              : toExperimentResults({
                  projectKey,
                  experimentKey: experiment?.key ?? '',
                  environmentKey: envKey,
                }),
          );
        }
      } finally {
        setIsUpdateExperimentStatusCallbacksPending(false);
      }
    },
    [
      onConfirm,
      startExperimentIteration,
      onStartSuccess,
      setIsModalOpen,
      location.pathname,
      navigate,
      isMAB,
      isHoldout,
      isRunning,
      projectKey,
      experiment?.key,
      envKey,
    ],
  );

  // A flag-backed experiment can't be started until we have both the flag rep and the iteration's
  // flag config; a warehouse-assigned one has neither and needs neither.
  if (!experiment || (flagKey && (!flagData || !iterationFlag))) {
    return null;
  }

  if (
    experiment?.currentIteration?.status === ExperimentStatusKind.NOT_STARTED ||
    experiment?.currentIteration?.status === ExperimentStatusKind.STOPPED
  ) {
    return (
      <ModalOverlay isOpen={isModalOpen} onOpenChange={handleOpenChange}>
        <Modal>
          <Dialog>
            <StartExperimentModal
              isPending={isPending}
              onCancel={() => {
                onCancel();
                setIsModalOpen(false);
              }}
              // We need to force create a new iteration when starting an experiment to get the latest flag config version
              onConfirm={() => handleStartExperiment(experiment.type !== 'holdout')}
              isMAB={isMAB}
              isHoldout={experiment.type === 'holdout'}
              isFlagOff={Boolean(isFlagOff)}
              experiment={experiment}
              flagKey={flagKey}
              hasStratifiedSampling={hasStratifiedSampling}
              assignsFromWarehouse={assignsFromWarehouse}
            />
          </Dialog>
        </Modal>
      </ModalOverlay>
    );
  }

  if (experiment?.currentIteration?.status === ExperimentStatusKind.RUNNING && isEditMode) {
    return (
      <ModalOverlay isOpen={isModalOpen} onOpenChange={handleOpenChange}>
        <Modal>
          <Dialog>
            <SaveExperimentModal
              onCancel={() => {
                onCancel();
                setIsModalOpen(false);
              }}
              isPending={isPending}
              onConfirm={({ reason }) => handleStartExperiment(false, { reason })}
              hasStratifiedSampling={hasStratifiedSampling}
            />
          </Dialog>
        </Modal>
      </ModalOverlay>
    );
  }

  return null;
}
